/**
 * Read-only role-capability matrix. Rendered straight from the backend
 * ``/api/rbac/matrix`` (which derives it from ROLE_CAPABILITIES), so it can't
 * drift from actual authorization. Viewer+.
 */

import { useEffect, useState } from "react";
import { Alert, Card, Spin, Table, Tag, Typography } from "antd";

import {
  ApiError,
  type RbacCapabilityRow,
  type RbacMatrix,
  api,
} from "../../api/client";

export default function RolesMatrixPage() {
  const [data, setData] = useState<RbacMatrix | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .rbacMatrix()
      .then(setData)
      .catch((e: unknown) =>
        setError(
          e instanceof ApiError ? e.detail : e instanceof Error ? e.message : String(e),
        ),
      );
  }, []);

  if (error) return <Alert type="error" message={error} showIcon />;
  if (!data) return <Spin />;

  const columns = [
    { title: "Capability", dataIndex: "label", key: "label" },
    ...data.roles.map((role) => ({
      title: role,
      key: role,
      align: "center" as const,
      render: (_: unknown, row: RbacCapabilityRow) =>
        row.roles.includes(role) ? (
          <Tag color="green">✓</Tag>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    })),
  ];

  return (
    <Card
      title="Role capability matrix"
      extra={<Typography.Text type="secondary">read-only</Typography.Text>}
    >
      <Table
        rowKey="key"
        dataSource={data.capabilities}
        columns={columns}
        pagination={false}
        size="small"
      />
    </Card>
  );
}
